import { useEffect, useState, type ComponentType } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useChatHint } from '../lib/morningChatHint';
import { useKeyboardOpen } from '../hooks/useKeyboardOpen';
import { ChatIcon, ConvoIcon, DealsIcon, MoreIcon, TasksIcon } from './chat/icons';

/**
 * CHAT-DUET (his words, 2026-10-28): "Below it, having the tasks and the different buttons right there" - and "the bar
 * gets Chat, Tasks, Convo, My Deals and More" (Convo: "instead of text, it should go to conversation ... convo for
 * short"). The Duet bar: a frosted 64px pill floating 16px from each side, above the home indicator.
 *  - Phones only (`md:hidden` - where the left menu folds into the drawer); More opens that same drawer.
 *  - Only for a person whose morning chat is on (the per-device hint) - Robert keeps his screens until his is on.
 *  - Never while a keyboard is up - but its room stays then, so the page under the field does not move; only for the
 *    Morning chat box (`data-sits-on-keyboard`) does the room go too, so the box comes down onto the keys.
 *  - It puts `has-tab-bar` on <html>, and index.css turns that into `--tab-bar-space` below 768px, so every page ends
 *    above it and the Morning chat box sits on it. No hint, or a wide screen: the space is 0.
 *  - ⛔ z-40: under every dialog (z-50), the Assistant's sheet (z-[1200]) and the drawer (z-[2100]) - it never covers them.
 */
export const TABS: ReadonlyArray<{ label: string; to: string; Icon: ComponentType<{ className?: string }> }> = [
  { label: 'Chat', to: '/morning', Icon: ChatIcon },
  { label: 'Tasks', to: '/todos', Icon: TasksIcon },
  { label: 'Convo', to: '/crm/conversations', Icon: ConvoIcon },
  { label: 'My Deals', to: '/dashboard', Icon: DealsIcon },
];

const isCurrent = (to: string, pathname: string): boolean =>
  to === '/crm/conversations' ? pathname.startsWith('/crm/conversations') : pathname === to;

/**
 * Below Tailwind's `md` (768px) - where the shell folds its left menu into the drawer. Decided HERE, not by a width rule
 * in index.css (that stylesheet allows none - index.css.test.ts), so the bar and the space it reserves always agree.
 * No matchMedia at all (a very old browser): treated as wide - no bar, the menu as today.
 */
const PHONE_WIDTH = '(max-width: 767.98px)';
function usePhoneWidth(): boolean {
  const query = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(PHONE_WIDTH) : null);
  const [narrow, setNarrow] = useState(() => query()?.matches ?? false);
  useEffect(() => {
    const mq = query();
    if (!mq) return undefined;
    const follow = () => setNarrow(mq.matches);
    mq.addEventListener('change', follow);
    follow();
    return () => mq.removeEventListener('change', follow);
  }, []);
  return narrow;
}

const TAB = 'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-[27px] transition-colors';
const LABEL = 'max-w-full truncate whitespace-nowrap text-[12px] leading-[15px]';

export default function PhoneTabBar({ onMore }: { onMore: () => void }) {
  const hint = useChatHint();
  const phone = usePhoneWidth();
  const keyboard = useKeyboardOpen();
  const { pathname } = useLocation();
  const shown = hint && phone && keyboard === 'none';
  // ⛔ Its ROOM stays while a keyboard is up (the bar only hides), so nothing under the field moves - except for a box
  //   that sits ON the keyboard (the Morning chat box): there the room goes, so the box comes down onto the keys.
  const reserve = hint && phone && keyboard !== 'dock';

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('has-tab-bar', reserve);
    return () => { root.classList.remove('has-tab-bar'); };
  }, [reserve]);

  if (!shown) return null;
  return (
    <>
    {/* Found by LOOKING (2026-10-28): floating with 16px either side and 12px under it, the bar let the page's cards show
        around and under it, and a scrap of their text in the gap above it. A strip of the page's own colour lies behind
        it - as in the mockup he approved - fading in over its top 12px. Taps pass through it; z-[39], under the bar. */}
    <div
      data-testid="tab-bar-backdrop"
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[39] h-[calc(88px+env(safe-area-inset-bottom))] bg-[linear-gradient(to_bottom,rgba(244,244,245,0),#F4F4F5_12px)] md:hidden"
    />
    <nav
      aria-label="Quick links"
      className="chat-glass shadow-chat-float fixed inset-x-4 bottom-[calc(12px+env(safe-area-inset-bottom))] z-40 flex h-16 gap-0.5 rounded-[32px] p-[5px] md:hidden"
    >
      {TABS.map(({ label, to, Icon }) => {
        const on = isCurrent(to, pathname);
        return (
          <Link
            key={to}
            to={to}
            aria-current={on ? 'page' : undefined}
            className={`${TAB} ${on ? 'bg-chat-capsule font-semibold text-chat-ink' : 'font-medium text-chat-ink-2 hover:text-chat-ink'}`}
          >
            <Icon className="h-[21px] w-[21px]" />
            <span className={LABEL}>{label}</span>
          </Link>
        );
      })}
      <button type="button" aria-label="More" onClick={() => onMore()} className={`${TAB} font-medium text-chat-ink-2 hover:text-chat-ink`}>
        <MoreIcon className="h-[21px] w-[21px]" />
        <span className={LABEL}>More</span>
      </button>
    </nav>
    </>
  );
}
